export type JwtClaims = Record<string, unknown> & {
  sub?: string;
  exp?: number;
  iat?: number;
  name?: string;
  email?: string;
};

const CLOCK_SKEW_MS = 5_000;

/** Decodifica o payload de um JWT. NÃO valida assinatura (papel do backend). */
export function decodeJwt(token: string): JwtClaims | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    return claims && typeof claims === "object" ? (claims as JwtClaims) : null;
  } catch {
    return null;
  }
}

/** Epoch ms de expiração, ou null quando o token não tem `exp` válido. */
export function getJwtExpiry(claims: JwtClaims): number | null {
  return typeof claims.exp === "number" && Number.isFinite(claims.exp) ? claims.exp * 1000 : null;
}

export function isExpired(expiresAt: number, now = Date.now()): boolean {
  return now >= expiresAt - CLOCK_SKEW_MS;
}

/** Normaliza claims de papel que podem vir como string ou array. */
export function readStringList(value: unknown): string[] {
  const list = Array.isArray(value) ? value : [value];
  return list
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Gera um JWT NÃO assinado — apenas para o adapter de desenvolvimento. */
export function encodeUnsignedJwt(claims: JwtClaims): string {
  const encode = (value: unknown) =>
    btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(value))))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  return `${encode({ alg: "none", typ: "JWT" })}.${encode(claims)}.`;
}
